<template>
  <div>
    <div class="pt-32pt">
      <div :class="containerClass" class="d-flex flex-column flex-md-row align-items-center text-center text-sm-left">
        <div class="flex d-flex flex-column flex-sm-row align-items-center">
          <div class="mb-24pt mb-sm-0 mr-sm-24pt">
            <page-heading class="mb-0">
              {{ title }}
              <i
                class="material-icons icon-16pt clickable-item"
                v-b-popover.hover.top="
                  `See your class schedule and your attendance. Scheduled class days show the session type and time; once your school marks attendance, the day also shows Present, Absent or Tardy.`
                "
                >info</i
              >
            </page-heading>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section">
      <div :class="{ container: !isMobileScreen }">
        <vue-cal
          :hideViewSelector="true"
          :time="false"
          :disable-views="['years', 'year', 'week', 'day']"
          :dblclick-to-navigate="false"
          active-view="month"
          style="height: 500px; border-radius: 12px"
        >
          <template #arrow-prev>
            <i
              :style="`color: ${textColorChanged(get(getLoggedInUser.linked_entity, 'primary_color'))}`"
              class="icon material-icons"
              >arrow_back</i
            >
          </template>
          <template #arrow-next>
            <i
              :style="`color: ${textColorChanged(get(getLoggedInUser.linked_entity, 'primary_color'))}`"
              class="icon material-icons"
              >arrow_forward</i
            >
          </template>
          <template #title="{ title }">
            <div :style="`color: ${textColorChanged(get(getLoggedInUser.linked_entity, 'primary_color'))}`">
              {{ title }}
            </div>
          </template>
          <template #cell-content="{ cell }">
            <div
              v-if="formatAttendance[cell.formattedDate]"
              class="d-flex flex-column align-items-center justify-content-center"
            >
              <div>
                {{ cell.content }}
              </div>
              <div
                v-if="formatAttendance[cell.formattedDate].status"
                class="d-flex align-items-center justify-content-center pill"
                :class="
                  formatAttendance[cell.formattedDate].status === 'P'
                    ? 'bg-success'
                    : formatAttendance[cell.formattedDate].status === 'A'
                    ? 'bg-danger'
                    : 'bg-warning'
                "
                :style="isMobileScreen ? 'font-size: 8px; height: 20px;' : 'font-size: 10px; height: 22px;'"
              >
                {{
                  formatAttendance[cell.formattedDate].status === 'P'
                    ? 'Present'
                    : formatAttendance[cell.formattedDate].status === 'A'
                    ? 'Absent'
                    : 'Tardy'
                }}
              </div>
              <div
                v-if="formatAttendance[cell.formattedDate].activity_category"
                class="mt-1"
                style="text-align: center; line-height: 1.3"
              >
                <span
                  :style="`display:inline-block;vertical-align:middle;width:8px;height:8px;border-radius:50%;margin-right:4px;background-color:${activityCategoryColor(
                    formatAttendance[cell.formattedDate].activity_category,
                  )};`"
                ></span><span
                  class="text-uppercase"
                  :style="
                    'vertical-align:middle;font-weight:600;letter-spacing:0.02em;color:#6c757d;' +
                    (isMobileScreen ? 'font-size: 8px;' : 'font-size: 10px;')
                  "
                  >{{ activityCategoryLabel(formatAttendance[cell.formattedDate].activity_category) }}</span
                >
              </div>
              <div
                v-if="formatAttendance[cell.formattedDate].start_time"
                class="mt-1"
                :style="'color: #6c757d;' + (isMobileScreen ? 'font-size: 8px;' : 'font-size: 9px;')"
              >
                {{ formatScheduleTime(formatAttendance[cell.formattedDate].start_time)
                }}<template v-if="formatAttendance[cell.formattedDate].end_time">
                  – {{ formatScheduleTime(formatAttendance[cell.formattedDate].end_time) }}</template
                >
              </div>
            </div>
            <div v-else>
              {{ cell.content }}
            </div>
          </template>
        </vue-cal>

        <!-- Legend: distinguishes the filled attendance-status pill from the
             outlined schedule-category badge shown inside each calendar day. -->
        <div class="d-flex flex-wrap align-items-center mt-3" style="gap: 8px 24px; font-size: 12px">
          <div class="d-flex align-items-center">
            <span
              class="d-inline-flex align-items-center justify-content-center bg-success"
              style="border-radius: 5px; color: #fff; font-weight: 700; padding: 2px 8px; font-size: 10px"
              >Present</span
            >
            <span class="ml-2 text-muted">Attendance status (filled)</span>
          </div>
          <div class="d-flex align-items-center">
            <span
              style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #6c757d"
            ></span>
            <span
              class="text-uppercase ml-1"
              style="font-weight: 600; letter-spacing: 0.02em; color: #6c757d; font-size: 10px"
              >Category</span
            >
            <span class="ml-2 text-muted">Scheduled class day (colored dot)</span>
          </div>
          <div class="text-muted">A day with a time but no status is scheduled and not marked yet.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, keyBy } from 'lodash';
import { activityCategoryColor, activityCategoryLabel } from '@/common/utils';
import Page from '@/components/Page.vue';
import { mapGetters, mapActions } from 'vuex';
import PageHeading from '@/components/Ui/PageHeading.vue';
import VueCal from 'vue-cal';
import 'vue-cal/dist/vuecal.css';
import moment from 'moment';

export default {
  components: { PageHeading, VueCal },
  extends: Page,

  data() {
    return {
      title: 'My Schedule and Attendance',
      windowWidth: window.innerWidth,
      attendance: [],
      schedule: [],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    // One entry per calendar day: scheduled class days (category + time) with the
    // marked attendance status layered on top when the school has recorded it.
    formatAttendance() {
      const days = {};
      this.schedule.forEach(day => {
        days[day.date] = { ...day, status: null };
      });
      this.attendance.forEach(log => {
        const scheduled = days[log.date] || {};
        days[log.date] = {
          ...scheduled,
          ...log,
          activity_category: log.activity_category || scheduled.activity_category,
          start_time: log.start_time || scheduled.start_time,
          end_time: log.end_time || scheduled.end_time,
        };
      });
      return days;
    },
    isMobileScreen() {
      return this.windowWidth <= 576;
    },
  },

  methods: {
    ...mapActions('attendance', ['getAttendance', 'getMySchedule']),
    get,
    keyBy,
    activityCategoryColor,
    activityCategoryLabel,
    formatScheduleTime(time) {
      return time ? moment(time, 'HH:mm:ss').format('h:mm A') : '';
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    textColorChanged(color) {
      if (color) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);

        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? 'black' : 'white';
      }
    },

    async fetchAttendance() {
      const res = await this.getAttendance();
      this.attendance = res.data.data;
    },

    async fetchSchedule() {
      // The schedule is extra context; if it fails, attendance still shows.
      try {
        const res = await this.getMySchedule();
        this.schedule = res.data.data || [];
      } catch (_err) {
        this.schedule = [];
      }
    },
  },

  async mounted() {
    this.fetchAttendance();
    this.fetchSchedule();

    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="css">
.pill {
  width: 70%;
  border-radius: 5px;
  color: white;
}

.vuecal__title-bar {
  background-color: var(--primary-color);
  border-radius: 12px 12px 0 0;
}

.vuecal__flex .vuecal__cell {
  border-radius: 12px;
}

.vuecal__flex .vuecal__flex {
  border-radius: 12px;
}
</style>
